import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';
import { cn } from '../../styles/lib/utils';

const boxVariants = cva('@container flex flex-col items-start bg-white border border-grey-30', {
  variants: {
    rounded: {
      sm: 'rounded-sm',
      lg: 'rounded-lg',
    },
  },
  defaultVariants: {
    rounded: 'sm',
  },
});

// Container that groups sections together
function Box({ className, rounded, ...props }: React.ComponentProps<'div'> & VariantProps<typeof boxVariants>) {
  return <div data-slot="box" className={cn(boxVariants({ rounded }), className)} {...props} />;
}

const boxHeaderVariants = cva('flex w-full', {
  variants: {
    variant: {
      default: 'p-3 pb-0',
      // A header bar with a divider under it, for a title with badges or an icon beside it
      bar: 'flex-wrap items-center gap-2 border-b border-grey-20 px-3.5 py-2.5',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

// First section with header text
function BoxHeader({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof boxHeaderVariants>) {
  return <div data-slot="box-header" className={cn(boxHeaderVariants({ variant }), className)} {...props} />;
}

const boxSectionVariants = cva('flex [&:not(:last-child)]:border-b w-full', {
  variants: {
    variant: {
      default: 'border-grey-30 p-3',
      // Tighter rows with lighter dividers, for lists of items
      row: 'border-grey-20 px-3.5 py-2.5',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

// Default section with border bottom
function BoxSection({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof boxSectionVariants>) {
  return <div data-slot="box-section" className={cn(boxSectionVariants({ variant }), className)} {...props} />;
}

const textVariants = cva('font-noto text-primary font-normal text-sm', {
  variants: {
    variant: {
      default: 'break-all',
      label: 'text-xs text-grey-60',
      title: 'text-base font-semibold',
      date: 'text-xs text-grey-70',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

function BoxText({ className, variant, ...props }: React.ComponentProps<'span'> & VariantProps<typeof textVariants>) {
  return <span data-slot="text" className={cn(textVariants({ variant, className }))} {...props} />;
}

export { Box, BoxHeader, BoxSection, BoxText };
